import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import "./GalleryCardMenu.css";

const POPOVER_GAP_PX = 8;
const VIEWPORT_MARGIN_PX = 8;

interface GalleryCardMenuProps {
  title: string;
  onShare?: () => void;
  shareTestId?: string;
  onRename?: () => void;
  onMakeCopy?: () => void;
  onDelete?: () => void;
}

export default function GalleryCardMenu({
  title,
  onShare,
  shareTestId,
  onRename,
  onMakeCopy,
  onDelete,
}: GalleryCardMenuProps) {
  const [open, setOpen] = useState(false);
  const [popoverStyle, setPopoverStyle] = useState<CSSProperties>({ visibility: "hidden" });
  const containerRef = useRef<HTMLSpanElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLSpanElement>(null);
  const firstItemRef = useRef<HTMLButtonElement>(null);
  const isInside = (node: Node | null) =>
    Boolean(node && (containerRef.current?.contains(node) || popoverRef.current?.contains(node)));

  // The card clips its content, so the popover is portalled to the canvas root
  // and pinned to the trigger. It opens below, or above when there is no room.
  useLayoutEffect(() => {
    if (!open) {
      setPopoverStyle({ visibility: "hidden" });
      return;
    }
    const trigger = triggerRef.current?.getBoundingClientRect();
    const popover = popoverRef.current?.getBoundingClientRect();
    if (!trigger || !popover) return;
    const below = trigger.bottom + POPOVER_GAP_PX;
    const fitsBelow = below + popover.height <= window.innerHeight - VIEWPORT_MARGIN_PX;
    const top = fitsBelow
      ? below
      : Math.max(VIEWPORT_MARGIN_PX, trigger.top - POPOVER_GAP_PX - popover.height);
    setPopoverStyle({
      top,
      left: Math.max(VIEWPORT_MARGIN_PX, trigger.right - popover.width),
    });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    firstItemRef.current?.focus();
    const close = () => setOpen(false);
    const onPointerDown = (event: PointerEvent) => {
      if (event.target instanceof Node && !isInside(event.target)) {
        const card = containerRef.current?.closest<HTMLElement>('[role="button"]');
        if (card?.contains(event.target)) {
          const suppressCardClick = (click: MouseEvent) => {
            window.clearTimeout(expiry);
            if (click.target instanceof Node && card.contains(click.target)) {
              click.preventDefault();
              click.stopPropagation();
            }
          };
          document.addEventListener("click", suppressCardClick, { capture: true, once: true });
          const expiry = window.setTimeout(() => {
            document.removeEventListener("click", suppressCardClick, true);
          }, 1000);
        }
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    const onScroll = (event: Event) => {
      if (!(event.target instanceof Node) || !popoverRef.current?.contains(event.target)) close();
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("keydown", onKeyDown, true);
    document.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("keydown", onKeyDown, true);
      document.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", close);
    };
  }, [open]);

  const portalTarget = containerRef.current?.closest<HTMLElement>(".excalidraw") ?? document.body;

  return (
    <span
      ref={containerRef}
      className="gallery-card-menu"
      data-app-usage-surface="management"
      onBlur={(event) => {
        if (!isInside(event.relatedTarget)) setOpen(false);
      }}
    >
      <button
        ref={triggerRef}
        type="button"
        className="dropdown-menu-button main-menu-trigger gallery-card-menu__trigger"
        aria-label={`More actions for ${title}`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <CanvasMenuIcon />
      </button>
      {open &&
        createPortal(
          <span
            ref={popoverRef}
            className="gallery-card-menu__popover"
            data-app-usage-surface="management"
            style={popoverStyle}
          >
            <span
              className="gallery-card-menu__items"
              role="menu"
              aria-label={`Actions for ${title}`}
              onKeyDown={(event) => {
                if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
                event.preventDefault();
                const items = Array.from(
                  event.currentTarget.querySelectorAll<HTMLButtonElement>("button"),
                );
                const index = items.indexOf(document.activeElement as HTMLButtonElement);
                items[
                  (index + (event.key === "ArrowDown" ? 1 : items.length - 1)) % items.length
                ]?.focus();
              }}
            >
              {onShare && (
                <button
                  ref={firstItemRef}
                  type="button"
                  role="menuitem"
                  className="gallery-card-menu__item"
                  aria-label={`Share ${title}`}
                  data-testid={shareTestId}
                  onClick={() => {
                    setOpen(false);
                    onShare();
                  }}
                >
                  <ShareIcon />
                  Share
                </button>
              )}
              {onRename && (
                <button
                  ref={onShare ? undefined : firstItemRef}
                  type="button"
                  role="menuitem"
                  className="gallery-card-menu__item"
                  aria-label={`Rename ${title}`}
                  onClick={() => {
                    setOpen(false);
                    onRename();
                  }}
                >
                  <RenameIcon />
                  Rename
                </button>
              )}
              {onMakeCopy && (
                <button
                  ref={onShare || onRename ? undefined : firstItemRef}
                  type="button"
                  role="menuitem"
                  className="gallery-card-menu__item"
                  aria-label={`Make a copy of ${title}`}
                  onClick={() => {
                    triggerRef.current?.focus();
                    setOpen(false);
                    onMakeCopy();
                  }}
                >
                  <DuplicateIcon />
                  Make a copy
                </button>
              )}
              {onDelete && (
                <button
                  ref={onShare || onRename || onMakeCopy ? undefined : firstItemRef}
                  type="button"
                  role="menuitem"
                  className="gallery-card-menu__item gallery-card-menu__item--danger"
                  aria-label={`Delete ${title}`}
                  onClick={() => {
                    setOpen(false);
                    onDelete();
                  }}
                >
                  <TrashIcon />
                  Delete
                </button>
              )}
            </span>
          </span>,
          portalTarget,
        )}
    </span>
  );
}

/** Excalidraw's main-menu (hamburger) icon; the package does not export it. */
function CanvasMenuIcon() {
  return (
    <svg
      className="gallery-card-menu__icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <line x1="4" y1="6" x2="20" y2="6" />
      <line x1="4" y1="12" x2="20" y2="12" />
      <line x1="4" y1="18" x2="20" y2="18" />
    </svg>
  );
}

/** Excalidraw's canvas menu item icon geometry (tabler-derived, 20×20). */
function MenuItemIcon({ children }: { children: ReactNode }) {
  return (
    <svg
      className="gallery-card-menu__item-icon"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

function ShareIcon() {
  return (
    <MenuItemIcon>
      <path
        strokeWidth="1.5"
        d="M5 12.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5ZM15 7.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5ZM15 17.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5ZM7.25 8.917l5.5-2.834M7.25 11.083l5.5 2.834"
      />
    </MenuItemIcon>
  );
}

function RenameIcon() {
  return (
    <MenuItemIcon>
      <path d="M11.25 5.417 14.583 8.75M3.333 16.667h3.334l8.75-8.75a2.357 2.357 0 0 0-3.334-3.334l-8.75 8.75v3.334Z" />
    </MenuItemIcon>
  );
}

function DuplicateIcon() {
  return (
    <MenuItemIcon>
      <path d="M14.375 6.458H8.958a2.5 2.5 0 0 0-2.5 2.5v5.417a2.5 2.5 0 0 0 2.5 2.5h5.417a2.5 2.5 0 0 0 2.5-2.5V8.958a2.5 2.5 0 0 0-2.5-2.5Z" />
      <path d="M11.667 3.125c.517 0 .986.21 1.325.55.34.338.55.807.55 1.325v1.458H8.333c-.485 0-.927.185-1.26.487-.343.312-.57.75-.609 1.24l-.005 5.357H5a1.87 1.87 0 0 1-1.326-.55 1.87 1.87 0 0 1-.549-1.325V5c0-.518.21-.987.55-1.326.338-.34.807-.549 1.325-.549h6.667Z" />
    </MenuItemIcon>
  );
}

function TrashIcon() {
  return (
    <MenuItemIcon>
      <path d="M3.333 5.833h13.334M8.333 9.167v5M11.667 9.167v5M4.167 5.833l.833 10c0 .92.746 1.667 1.667 1.667h6.666c.92 0 1.667-.746 1.667-1.667l.833-10M7.5 5.833v-2.5c0-.46.373-.833.833-.833h3.334c.46 0 .833.373.833.833v2.5" />
    </MenuItemIcon>
  );
}
